/**
 * LearningPanel component for chat-first learning.
 * Opens in the right side panel (like ResumePreviewPanel) when
 * AI detects a learning intent in chat.
 */

import { cn } from '@/lib/utils'
import type { LearningContentEvent } from '@/types/learning'
import { BookOpen, CheckCircle2, X } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Button } from './ui/button'

interface LearningPanelProps {
  content: LearningContentEvent | null
  isLoading: boolean
  onClose: () => void
  onMarkComplete?: () => void
  className?: string
}

export function LearningPanel({
  content,
  isLoading,
  onClose,
  onMarkComplete,
  className,
}: LearningPanelProps) {
  const isComplete = content?.progress?.status === 'completed'
  const progressPercent = content?.progress?.progress_percent ?? 0

  return (
    <div className={cn('h-full flex flex-col bg-gray-50 relative', className)}>
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 z-10 overflow-hidden">
          <div className="absolute inset-0 bg-white/80 backdrop-blur-[2px]" />

          <div
            className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald-400 to-transparent opacity-60"
            style={{ animation: 'scan 2s ease-in-out infinite' }}
          />

          <div className="absolute inset-0 flex items-center justify-center">
            <div className="relative">
              <div
                className="absolute inset-0 rounded-full border-2 border-emerald-400/30"
                style={{
                  width: '80px',
                  height: '80px',
                  left: '-20px',
                  top: '-20px',
                  animation: 'pulse-ring 1.5s ease-out infinite',
                }}
              />
              <div
                className="absolute inset-0 rounded-full border-2 border-emerald-400/20"
                style={{
                  width: '80px',
                  height: '80px',
                  left: '-20px',
                  top: '-20px',
                  animation: 'pulse-ring 1.5s ease-out infinite 0.5s',
                }}
              />
              <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                <svg
                  className="w-5 h-5 text-white"
                  style={{ animation: 'spin 2s linear infinite' }}
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <style>{`
            @keyframes scan {
              0% { top: 0%; opacity: 0; }
              10% { opacity: 0.6; }
              90% { opacity: 0.6; }
              100% { top: 100%; opacity: 0; }
            }
            @keyframes pulse-ring {
              0% { transform: scale(0.8); opacity: 0.8; }
              100% { transform: scale(1.5); opacity: 0; }
            }
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      )}

      {/* Header */}
      <div className="p-4 border-b bg-white flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <BookOpen className="h-5 w-5 text-emerald-600 shrink-0" />
          <div className="min-w-0">
            <h3 className="font-medium text-gray-900 truncate">
              {content?.topic_title || 'Learning'}
            </h3>
            {content && content.total_modules > 0 && (
              <p className="text-xs text-gray-500">
                Module {content.order_index + 1} of {content.total_modules}
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 hover:bg-gray-100 rounded-md transition-colors shrink-0"
          title="Close panel"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {/* Module title bar */}
      {content?.module_title && (
        <div className="px-4 py-2 bg-emerald-50 border-b border-emerald-100 text-sm">
          <span className="text-emerald-700 font-medium">{content.module_title}</span>
        </div>
      )}

      {/* Scrollable content area */}
      <div className="flex-1 overflow-y-auto">
        {content?.content ? (
          <div className="p-5 prose prose-sm max-w-none prose-headings:text-gray-900 prose-p:text-gray-700 prose-li:text-gray-700 prose-strong:text-gray-900">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{content.content}</ReactMarkdown>
          </div>
        ) : !isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <BookOpen className="h-12 w-12 text-gray-300 mb-4" />
            <p className="text-gray-500">Ask the AI to teach you something</p>
            <p className="text-sm text-gray-400 mt-2">Try "teach me about..." in the chat</p>
          </div>
        ) : null}
      </div>

      {/* Footer with progress */}
      {content?.module_id && (
        <div className="p-4 border-t bg-white shrink-0 space-y-3">
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={cn(
                'h-2 rounded-full transition-all duration-500',
                isComplete ? 'bg-emerald-500' : 'bg-emerald-400',
              )}
              style={{ width: `${Math.max(progressPercent, 5)}%` }}
            />
          </div>

          <Button
            onClick={onMarkComplete}
            disabled={isComplete}
            variant={isComplete ? 'outline' : 'default'}
            className={cn('w-full', !isComplete && 'bg-emerald-600 hover:bg-emerald-700')}
          >
            {isComplete ? (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4 text-emerald-600" />
                Completed
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Mark Complete
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
